// General
import "./preview-verified-photo-dialog.scss";
import { useEffect } from "react";
// Services
import {
  useUploadVp3PhotoMutation,
  useLazyGetProfileQuery,
} from "../../../../services/data.service";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { updatePreviewVerifiedPhotoDialog } from "../../../../redux/store/dialogStore";
// browser-image-compression
import imageCompression from "browser-image-compression";
// react-gtm-module
import TagManager from "react-gtm-module";
// Material UI
import { Dialog, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import IconManager from "../../../utility/manager/icon-manager/icon-manager";
// Components
import Spinner from "../../elements/spinner/spinner";

const PreviewVerifiedPhotoDialog = () => {
  // API variables
  const [
    uploadVp3Photo,
    {
      data: uploadVp3PhotoData,
      error: uploadVp3PhotoErrorData,
      isLoading: uploadVp3PhotoLoading,
      isSuccess: uploadVp3PhotoSuccess,
      isError: uploadVp3PhotoError,
    },
  ] = useUploadVp3PhotoMutation();
  const [
    getProfile,
    {
      data: getProfileData,
      error: getProfileErrorData,
      isFetching: getProfileFetching,
      isLoading: getProfileLoading,
      isSuccess: getProfileSuccess,
      isError: getProfileError,
    },
  ] = useLazyGetProfileQuery();

  // Redux variables
  const previewVerifiedPhotoDialog = useSelector(
    (state) => state.dialog.previewVerifiedPhotoDialog
  );
  const profilePreviewVerifiedPhotoInfo = useSelector(
    (state) => state.profile.profilePreviewVerifiedPhotoInfo
  );
  const dispatch = useDispatch();

  // MUI variables
  const isMobile = useMediaQuery("(max-width: 720px)");

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!previewVerifiedPhotoDialog) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-PreviewPhoto-Dialog",
      },
    });
  }, [previewVerifiedPhotoDialog]);

  // Lifecycle | Check for update | Update User Photo API Response
  useEffect(() => {
    if (uploadVp3PhotoLoading) {
    } else if (uploadVp3PhotoSuccess) {
      switch (uploadVp3PhotoData?.status) {
        case 0:
        case 1:
          getProfile("?photo=true", false);
          onCloseDialog();
          break;
        default:
          break;
      }
    } else if (uploadVp3PhotoError) {
    }
  }, [uploadVp3PhotoLoading, uploadVp3PhotoSuccess, uploadVp3PhotoError]);

  // Event Handlers | Button
  const onUploadVpButton = async (event) => {
    const file = event.target.files[0];

    const options = {
      maxSizeMB: 1,
    };

    if (file !== undefined) {
      try {
        const compressedFile = await imageCompression(file, options);
        const imageData = await readFile(compressedFile);
        const obj = {
          photo_base64: imageData,
          async: true,
        };
        uploadVp3Photo(obj);
      } catch (error) {
        console.log(error);
      }
    }
  };

  // Event Handlers | MUI Dialog
  const onCloseDialog = () => {
    dispatch(updatePreviewVerifiedPhotoDialog(false));
  };

  // Utility Functions
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <Dialog
      className="custom-fullscreen-transparent-dialog custom-dialog-height-inherit"
      fullScreen={isMobile ? true : false}
      open={previewVerifiedPhotoDialog}
      onClose={onCloseDialog}
    >
      <div id="preview-verified-photo-dialog">
        <div className="dialog-header">
          <div className="close-button-container" onClick={onCloseDialog}>
            <CloseIcon className="close-button" />
          </div>
        </div>

        <div className="profile-photo-container">
          <img
            className={`profile-photo ${
              isMobile ? "dynamic-width" : "fixed-width"
            }`}
            src={profilePreviewVerifiedPhotoInfo?.photoUrl}
            alt="profile-photo"
          />

          {profilePreviewVerifiedPhotoInfo?.showVpLabel && (
            <div className="verified-photo-label-container">
              {getIcon("verifiedProfileBadge", "verified-profile-badge")}
              <div className="verified-photo-label">
                {t("profile.photo_has_verified")}
              </div>
            </div>
          )}
        </div>

        {profilePreviewVerifiedPhotoInfo?.showUploadVpButton && (
          <div className="upload-vp-button-container">
            <input
              type="file"
              accept="image/*"
              id="upload-vp-input"
              style={{ display: "none" }}
              onChange={onUploadVpButton}
            />
            <label htmlFor="upload-vp-input">
              <div className="upload-vp-button">
                {uploadVp3PhotoLoading ? (
                  <Spinner
                    isPadding={false}
                    size={20}
                    color={"white-spinner"}
                  />
                ) : (
                  t("registration.photos.upload_new_photo")
                )}
              </div>
            </label>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PreviewVerifiedPhotoDialog;
